import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUturnLeftIcon, CreditCardIcon, UsersIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import Toggle from '../../utils/Toggle';
import { toast } from 'react-toastify';
import { find, propEq } from 'rambda';
import ClientModule from './ClientModule';
import ClientBilling from './ClientBilling';
import ClientDeleteConfirm from './ClientDeleteConfirm';
import ClientStripeSubscription from './ClientStripeSubscription';

const tabs = [
	{ name: 'Details', href: '#', current: true, icon: BuildingOfficeIcon },
	{ name: 'Modules', href: '#', current: false, icon: BuildingOfficeIcon },
	{ name: 'Users', href: '#', current: false, icon: UsersIcon },
	{ name: 'Billing', href: '#', current: false, icon: CreditCardIcon }
];

const modules = [
	{
		name: 'Action Items',
		description: 'Track Action Items by assignee',
		feature: 'ACTION_ITEMS',
		id: 1000,
	},
	{
		name: 'Communication',
		description: 'Peer-to-peer communication rooms',
		feature: 'COMMUNICATION',
		id: 1001,
	},
	{
		name: 'Driver Impact',
		description: 'Driver impact module',
		feature: 'IMPACT',
		id: 1002,
	},
	{
		name: 'Evaluation',
		description: 'Employee evaluation features',
		feature: 'EVALUATION',
		id: 1003,
	},
	{
		name: 'Goal Auto Link',
		description: 'Automatically link goals by category',
		feature: 'GOAL_AUTO_LINK',
		id: 1004,
	},
	{
		name: 'Meetings',
		description: 'Meetings and Agendas module',
		feature: 'MEETINGS',
		id: 1005,
	},
	{
		name: 'New Home Page',
		description: 'Enable use of the new homepage',
		feature: 'NEW_HOME_PAGE',
		id: 1006,
	},
	{
		name: 'Plan',
		description: 'Plans components including Plans, Sub Plans, Plan Goals, and KPIs features',
		feature: 'PLAN',
		id: 1007,
	},
	{
		name: 'Report',
		description: 'Reporting components and features',
		feature: 'REPORT',
		id: 1008,
	},
	{
		name: 'Responsibilities',
		description: 'Enable use of role responsibilities',
		feature: 'RESPONSIBILITY',
		id: 1009,
	},
	{
		name: 'Tone Analysis',
		description: 'Enable IBM Watson tone analysis',
		feature: 'TONE_ANALYSIS',
		id: 1010,
	},
	{
		name: 'UI',
		description: 'Enable the UI tab on the Settings page.',
		feature: 'UI',
		id: 1011,
	},
];

function classNames(...classes) {
	return classes.filter(Boolean).join(' ');
}

const clientDetailsUrl = id => `/client/v1/clientDetails/${id}`;
const clientSettingsUrl = id => `/accountsettings/v1/settings?clientId=${id}`;
const clientUpdateSettingsUrl = id => `/accountsettings/v1/settings/${id}`;
const clientProductUrl = (id, productId) => `/client/v1/client/${id}/product/${productId}`;

const ClientDetailsPage = ({ client, settings }) => {
	let navigate = useNavigate();

	const [currentTab, setCurrentTab] = useState(tabs[0]);
	const [status, setStatus] = useState(client.status);
	const [isClient, setIsClient] = useState(client.isClient);
	const [hasAlign, setHasAlign] = useState(client.scope.includes('hermes'));
	const [hasDrive, setHasDrive] = useState(client.scope.includes('janus'));
	const [nightlyEmail, setNightlyEmail] = useState(settings.enableNightlyNotificationEmail);
	const [nightlyEmailHour, setNightlyEmailHour] = useState(settings.notificationEmailHourUTC);
	const [accessValidity, setAccessValidity] = useState(client.accessTokenValidity);
	const [refreshValidity, setRefreshValidity] = useState(client.refreshTokenValidity);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [reactiveTabs, setReactiveTabs] = useState(tabs);

	useEffect(() => {
		if(client.hasSubscription) {
			setReactiveTabs([
				...tabs,
				{name: 'Stripe Subscription', href: '#', current: false, icon: CurrencyDollarIcon}
			]);

		}
		const item = find(propEq('current', true), tabs);
		if (item) {
			item.current = false;
		}
		tabs[0].current = true;
		setCurrentTab(tabs[0]);
	}, []);

	function onSelectTab(name) {
		console.log(name)
		const item = find(propEq('name', name), reactiveTabs);
		currentTab.current = false;
		item.current = true;
		setCurrentTab(item);
	}

	function handleEnableAlign() {
		setHasAlign(!hasAlign);
		const method = !hasAlign ? 'POST' : 'DELETE';
		setProduct(method, 1);
	}

	function handleEnableDrive() {
		setHasDrive(!hasDrive);
		const method = !hasDrive ? 'POST' : 'DELETE';
		setProduct(method, 2);
	}

	function setProduct(method, productId) {
		axios({
			method: method,
			url: clientProductUrl(client.id, productId),
		}).then(
			response => {
				toast('Product updated', {
					autoClose: 6000,
					type: toast.TYPE.SUCCESS,
				});
			},
			error => {
				console.log(error);
				toast('Product not updated', {
					autoClose: 6000,
					type: toast.TYPE.ERROR,
				});
			},
		);
	}

	function handleNightlyEmail() {
		const data = {
			enableNightlyNotificationEmail: !nightlyEmail,
		};
		updateSettings(data);
		setNightlyEmail(!nightlyEmail);
	}

	function handleNightlyEmailHour(hour) {
		const data = {
			notificationEmailHourUTC: hour,
		};
		updateSettings(data);
		setNightlyEmailHour(hour);
	}

	function updateSettings(data) {
		axios({
			method: 'PATCH',
			url: clientUpdateSettingsUrl(settings.id),
			data: data,
		}).then(
			() => {
				toast('Settings updated', {
					autoClose: 6000,
					type: toast.TYPE.SUCCESS,
				});
			},
			error => {
				console.log(error);
				toast('Settings not updated', {
					autoClose: 6000,
					type: toast.TYPE.ERROR,
				});
			},
		);
	}

	function handleRefreshValidChange(e) {
		setRefreshValidity(e.target.value);
		updateClient({ refreshTokenValidity: refreshValidity });
	}

	function handleAccessValidChange(e) {
		setAccessValidity(e.target.value);
		updateClient({ accessTokenValidity: accessValidity });
	}

	function updateClient(property) {
		axios({
			method: 'PATCH',
			url: clientDetailsUrl(client.id),
			data: property,
		}).then(
			() => {
				toast('Client updated', {
					autoClose: 6000,
					type: toast.TYPE.SUCCESS,
				});
			},
			error => {
				console.log(error);
				toast('Client not updated', {
					autoClose: 6000,
					type: toast.TYPE.ERROR,
				});
			},
		);
	}

	function handleBack() {
		navigate(-1);
	}

	function confirmDelete() {
		setShowDeleteConfirm(true);
	}

	return (
		<>
			{showDeleteConfirm && (
				<ClientDeleteConfirm clientId={client.id} open={showDeleteConfirm} setOpen={setShowDeleteConfirm} />
			)}
			<div className="py-4 px-4">
				<div className="pb-4" onClick={handleBack}>
					<span className="text-indigo-600">
						<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
					</span>
				</div>
				<h1>
					<strong>{client.client_id}</strong> {currentTab.name}
				</h1>
				<div className="sm:block">
					<div className="sm:visible border-b border-gray-200">
						<nav className="-mb-px flex space-x-8" aria-label="Tabs">
							{reactiveTabs.map(tab => (
								<div
									key={tab.name}
									onClick={e => {
										e.preventDefault();
										onSelectTab(tab.name);
									}}
									className={classNames(
										tab.current
											? 'border-indigo-500 text-indigo-600'
											: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
										'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm',
									)}
									aria-current={tab.current ? 'page' : undefined}>
									<tab.icon
										className={classNames(
											tab.current ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
											'-ml-0.5 mr-2 h-5 w-5',
										)}
										aria-hidden="true"
									/>
									<span>{tab.name}</span>
								</div>
							))}
						</nav>
					</div>
					<div className="py-4">
						{currentTab.name === 'Details' && (
							<form>
								<div className="px-4 space-y-8 divide-y divide-gray-200">
									<div className="pt-8">
										<div>
											<h3 className="text-lg font-medium leading-6 text-gray-900">Status</h3>
										</div>
										<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
											<div className="sm:col-span-3">
												<Toggle
													label="Status"
													value={status}
													onChangeValue={e => {
														setStatus(e ? 1 : 0);
														updateClient({ status: e ? 1 : 0 });
													}}
												/>
												<Toggle
													label="Client"
													value={isClient}
													onChangeValue={e => {
														setIsClient(e ? 1 : 0);
														updateClient({ isClient: e ? 1 : 0 });
													}}
												/>
											</div>
										</div>
										<div className="py-4">
											<button
												onClick={() => {
													confirmDelete();
												}}
												type="button"
												className="inline-flex items-end justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto">
												Delete Client
											</button>
										</div>
									</div>

									<div className="pt-8">
										<div>
											<h3 className="text-lg font-medium leading-6 text-gray-900">Products</h3>
										</div>
										<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
											<div className="sm:col-span-3">
												<Toggle
													label="Align"
													value={hasAlign}
													onChangeValue={handleEnableAlign}
												/>
												<Toggle
													label="Drive"
													value={hasDrive}
													onChangeValue={handleEnableDrive}
												/>
											</div>
										</div>
									</div>

									<div className="pt-8">
										<div>
											<h3 className="text-lg font-medium leading-6 text-gray-900">
												Nightly Email
											</h3>
										</div>
										<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
											<div className="sm:col-span-1">
												<Toggle
													label="Nightly Email Notifications"
													value={nightlyEmail}
													onChangeValue={handleNightlyEmail}
												/>
											</div>
										</div>
										<div className="w-1/6 col-span-1">
											<div className="py-6 col-span-1">
												<label
													htmlFor="meta"
													className="block text-sm font-medium text-gray-700">
													Notification Hour (UTC)
												</label>
												<div className="mt-1">
													<label htmlFor="notificationHour" className="sr-only">
														Quantity
													</label>
													<select
														value={nightlyEmailHour}
														onChange={e => {
															console.log(e.target.value);
															handleNightlyEmailHour(e.target.value);
														}}
														id="notificationHour"
														name="notificationHour"
														className="block max-w-full rounded-md border border-gray-300 py-1.5 text-left text-base font-medium leading-5 text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
														<option value={1}>1</option>
														<option value={2}>2</option>
														<option value={3}>3</option>
														<option value={4}>4</option>
														<option value={5}>5</option>
														<option value={6}>6</option>
														<option value={7}>7</option>
														<option value={8}>8</option>
														<option value={9}>9</option>
														<option value={10}>10</option>
														<option value={11}>11</option>
														<option value={12}>12</option>
														<option value={13}>13</option>
														<option value={14}>14</option>
														<option value={15}>15</option>
														<option value={16}>16</option>
														<option value={17}>17</option>
														<option value={18}>18</option>
														<option value={19}>19</option>
														<option value={20}>20</option>
														<option value={21}>21</option>
														<option value={22}>22</option>
														<option value={23}>23</option>
														<option value={24}>24</option>
													</select>
												</div>
											</div>
											<div className="sm:col-span-3"></div>
										</div>
									</div>
									<div className="pt-8">
										<div>
											<h3 className="text-lg font-medium leading-6 text-gray-900">
												Authorization Token Validity
											</h3>
										</div>
										<div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
											<div className="sm:col-span-1">
												<label
													htmlFor="meta"
													className="block text-sm font-medium text-gray-700">
													Token Validity (seconds)
												</label>
												<input
													value={accessValidity}
													onChange={e => {
														handleAccessValidChange(e);
													}}
													type="text"
													name="meta"
													id="meta"
													className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												/>
											</div>
											<div className="sm:col-span-1">
												<label
													htmlFor="meta"
													className="block text-sm font-medium text-gray-700">
													Refresh Token Validity (seconds)
												</label>
												<input
													value={refreshValidity}
													onChange={e => {
														handleRefreshValidChange(e);
													}}
													type="text"
													name="meta"
													id="meta"
													className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
												/>
											</div>
										</div>
									</div>
								</div>
							</form>
						)}

						{currentTab.name === 'Modules' && (
							<div className="overflow-hidden rounded-md bg-white shadow">
								<ul className="divide-y divide-gray-200">
									{modules.map(item => (
										<ClientModule
											key={item.id}
											module={item}
											modules={client.modules}
											clientId={client.id}
										/>
									))}
								</ul>
							</div>
						)}

						{currentTab.name === 'Users' && <div>Users</div>}

						{currentTab.name === 'Billing' && (
							<div className="overflow-hidden rounded-md bg-white shadow">
								<ClientBilling client={client} />
							</div>
						)}
						{currentTab.name === 'Stripe Subscription' && (
							<div className="overflow-hidden rounded-md bg-white shadow">
								<ClientStripeSubscription client={client} />
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const LoadingClient = () => {
	return <div></div>;
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const ClientDetails = () => {
	const [client, setClient] = useState(null);
	const [settings, setSettings] = useState(null);

	let { clientId } = useParams();

	useEffect(() => {
		const fetchClient = async () => {
			const response = await axios({
				method: 'GET',
				url: clientDetailsUrl(clientId),
			});

			setClient(response.data);
		};

		const fetchSettings = async () => {
			const response = await axios({
				method: 'GET',
				url: clientSettingsUrl(clientId),
			});

			setSettings(response.data);
		};

		if (clientId) {
			fetchClient().catch(console.error);
			fetchSettings().catch(console.error);
		}
	}, [clientId]);

	if (clientId && !client) {
		return <LoadingClient />;
	}

	if (client && settings) {
		return <ClientDetailsPage client={client} settings={settings} />;
	}
};

export default ClientDetails;
